import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Skeleton, Tooltip, Checkbox } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { listTenants } from '../api/tenant';
import type { TenantListItem } from '../api/tenant';
import { Topbar } from '../layouts/Topbar';
import { TenantStatusBadge } from '../components/StatusBadge';
import type { TenantStatus } from '../api/types';
import { formatDate } from '../utils/datetime';

const STATUS_LABEL: Record<TenantStatus, string> = {
  active: '活跃',
  grace: '宽限期',
  read_only: '只读',
  suspended: '已停服',
  manually_locked: '手动冻结',
  provisioning: '开通中',
  terminated: '已清退',
};

const STATUS_OPTIONS: Array<{ value: TenantStatus; label: string }> = (
  Object.entries(STATUS_LABEL) as [TenantStatus, string][]
).map(([value, label]) => ({ value, label }));

const PLAN_OPTIONS = ['standard', 'pro', 'enterprise'] as const;

const PAGE_SIZE = 20;

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function daysLeft(endAt: string | undefined | null): number {
  if (!endAt) return Number.POSITIVE_INFINITY;
  const ms = Date.parse(endAt) - Date.now();
  return Math.ceil(ms / 86400000);
}

export function TenantsListPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TenantStatus | ''>('');
  const [plan, setPlan] = useState<string>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['tenants', { status, search }],
    queryFn: () => listTenants({ status: status || undefined, search: search || undefined }),
  });

  const items: TenantListItem[] = data ?? [];

  // 套餐筛选（前端过滤，listTenants 不支持 plan 参数）
  const filtered = useMemo(() => {
    if (!plan) return items;
    return items.filter((t) => t.currentContract?.planCode === plan);
  }, [items, plan]);

  // 分页
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE),
    [filtered, pageSafe],
  );

  // KPI summary（基于全量未筛选）
  const yearMonth = currentYearMonth();
  const summary = {
    total: items.length,
    newThisMonth: items.filter((t) => t.created_at?.slice(0, 7) === yearMonth).length,
    expiring30: items.filter((t) => {
      const d = daysLeft(t.currentContract?.endAt);
      return d >= 0 && d <= 30;
    }).length,
  };

  function toggleAllOnPage(checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const t of pageItems) checked ? next.add(t.id) : next.delete(t.id);
      return next;
    });
  }
  function toggleOne(id: number, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((t) => selected.has(t.id));
  const someOnPageSelected = pageItems.some((t) => selected.has(t.id)) && !allOnPageSelected;

  return (
    <>
      <Topbar crumbs={[{ label: '运营' }, { label: '租户管理' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>租户管理</h1>
            <div className="page-header__sub">
              管理所有租户的基本信息、套餐、合同与生命周期。
              <Tooltip title="用户/渠道/消息列均由 metrics_sync 任务每 5 分钟从租户库聚合到 meta_db.tenant_metrics。">
                <span style={{ marginLeft: 8, padding: '2px 8px', background: '#E0F2FE', color: '#075985', fontSize: 11, borderRadius: 999, fontWeight: 500, cursor: 'help' }}>⟳ 用量列 5 分钟同步</span>
              </Tooltip>
            </div>
          </div>
          <div className="page-header__actions">
            <Link to="/tenants/new" className="btn btn-primary">
              <PlusOutlined /> <span>新建租户</span>
            </Link>
          </div>
        </div>

        <div className="summary-row">
          <div className="kpi">
            <div className="kpi__label">总租户</div>
            <div className="kpi__value">{summary.total}</div>
            <div className="kpi__delta flat">active + 非 active</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">当月新建</div>
            <div className="kpi__value">{summary.newThisMonth}</div>
            <div className="kpi__delta flat">{yearMonth}</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">即将到期 (30 天)</div>
            <div className="kpi__value">{summary.expiring30}</div>
            <div className="kpi__delta flat">含已进入宽限期</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">总用户数</div>
            <div className="kpi__value">—</div>
            <div className="kpi__delta flat">跨所有租户</div>
          </div>
        </div>

        <div className="card">
          <div className="card__body" style={{ paddingBottom: 0 }}>
            <div className="toolbar">
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type="search"
                  placeholder="搜索租户 code / 名称 / DB 名…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{ paddingLeft: 36, minWidth: 280 }}
                />
                <SearchOutlined style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-fg-3)' }} />
              </div>
              <select className="select" value={status} onChange={(e) => { setStatus(e.target.value as TenantStatus | ''); setPage(1); }}>
                <option value="">全部状态</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className="select" value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }}>
                <option value="">全部套餐</option>
                {PLAN_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {status && (
                <span className="filter-chip">
                  状态: {STATUS_LABEL[status]}
                  <span className="filter-chip__x" onClick={() => setStatus('')}>×</span>
                </span>
              )}
              {plan && (
                <span className="filter-chip">
                  套餐: {plan}
                  <span className="filter-chip__x" onClick={() => setPlan('')}>×</span>
                </span>
              )}
              {search && (
                <span className="filter-chip">
                  搜索: {search}
                  <span className="filter-chip__x" onClick={() => setSearch('')}>×</span>
                </span>
              )}
              <div className="toolbar__right">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => refetch()} disabled={isFetching}>
                  <ReloadOutlined spin={isFetching} /> <span>刷新</span>
                </button>
              </div>
            </div>
          </div>

          {isLoading ? <div style={{ padding: 24 }}><Skeleton active /></div> : (
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <Checkbox
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected}
                        onChange={(e) => toggleAllOnPage(e.target.checked)}
                      />
                    </th>
                    <th>租户</th>
                    <th>套餐</th>
                    <th>状态</th>
                    <th>合同到期</th>
                    <th>
                      用户
                      <Tooltip title="数据来自 meta_db.tenant_metrics，由 metrics_sync 任务每 5 分钟从租户库聚合一次。">
                        <span className="col-info-dot">i</span>
                      </Tooltip>
                    </th>
                    <th>
                      渠道
                      <Tooltip title="同步自租户库 channels 表，每 5 分钟更新一次。">
                        <span className="col-info-dot">i</span>
                      </Tooltip>
                    </th>
                    <th className="num">
                      7d 消息
                      <Tooltip title="同步自租户库 messages 表，统计近 7 天消息量。">
                        <span className="col-info-dot">i</span>
                      </Tooltip>
                    </th>
                    <th className="actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Checkbox
                          checked={selected.has(t.id)}
                          onChange={(e) => toggleOne(t.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <div className="text-strong">{t.display_name}</div>
                        <div className="text-xs text-muted text-mono">
                          <a
                            href={`https://${t.code}.owlsome.kzdatong.com`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >{t.code}.owlsome.kzdatong.com</a> · {t.db_name}
                        </div>
                      </td>
                      <td>{t.currentContract?.planCode && <span className="tag">{t.currentContract.planCode}</span>}</td>
                      <td><TenantStatusBadge status={t.status} /></td>
                      <td className="text-mono num">{formatDate(t.currentContract?.endAt)}</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="actions">
                        <Link to={`/tenants/${t.id}`} className="btn btn-ghost btn-sm">详情</Link>
                        <Link to={`/tenants/${t.id}/renew`} className="btn btn-ghost btn-sm">续签</Link>
                      </td>
                    </tr>
                  ))}
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无数据</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="pagination">
            <span className="text-muted">共 <strong>{total}</strong> 条 · 每页 {PAGE_SIZE}</span>
            <span style={{ margin: '0 12px' }} />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >‹</button>
            <span className="page-num is-active">{pageSafe}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >›</button>
          </div>
        </div>
      </div>
    </>
  );
}
